import {
  Button as ButtonActionButtons,
  ButtonVariants,
} from '@florencelabs/button';
import { twMerge } from '@florencelabs/fabric-styles';
import { MaterialSymbolSize } from '@florencelabs/icons';
import { TooltipButton } from '@florencelabs/tooltip';
import { isEqual } from 'lodash';
import { ReactElement, cloneElement, memo, useContext } from 'react';
import { DateRangePickerStateContext } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { DateRangePickerState } from 'react-stately';
import { useAvailabilityWindow } from '@provider-directory/hooks/useAvailabilityWindow';
import {
  Button as BCustom,
  ButtonProps,
} from './date-range-picker-custom-button';

export const GroupWrapper = ({
  children,
  className,
}: {
  className?: string;
  children: ReactElement;
}): ReactElement => {
  const stateCalendar = useContext(DateRangePickerStateContext);

  const handleClick = () => {
    if (!stateCalendar) {
      return;
    }

    if (stateCalendar.isOpen) {
      stateCalendar.close();
    } else {
      stateCalendar.open();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type={'button'}
      aria-expanded={stateCalendar?.isOpen}
      aria-haspopup={'dialog'}
      aria-label={'Select date range'}
      className={twMerge(
        'z-1 group/actions relative flex w-full cursor-pointer items-center p-1 hover:text-primary-600',
        'outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600',
        stateCalendar?.isOpen && 'text-primary-600',
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className={'absolute inset-0 z-0'} tabIndex={-1} />
      {children}
    </button>
  );
};
export const ButtonWrapper = ({
  onClick,
  variant,
  cta,
  type,
  className,
}: {
  className?: string;
  onClick: () => void;
  variant?: (typeof ButtonVariants)[number];
  cta?: string;
  type?: 'button' | 'reset' | 'button';
}): ReactElement => {
  return (
    <ButtonActionButtons
      aria-label={`${cta} date range selection`}
      testId={`date-range-${cta}-btn`}
      className={className}
      variant={variant || 'systemEmpty'}
      type={type || 'button'}
      onPress={() => {
        onClick();
      }}
    >
      {cta}
    </ButtonActionButtons>
  );
};

export const CustomButtonActions = memo(
  ({
    onClick,
    children,
    disabled,
    className,
    isNextAction = false,
    messageToken,
    iconSize,
    ariaLabel,
  }: {
    className?: string;
    onClick: (calendarState: DateRangePickerState) => void;
    children: ReactElement<ButtonProps>;
    disabled: boolean;
    isNextAction?: boolean;
    messageToken?: string;
    iconSize?: MaterialSymbolSize;
    ariaLabel?: string;
  }): ReactElement => {
    const { t } = useTranslation();
    const stateCalendar = useContext(DateRangePickerStateContext);
    const maxDays = useAvailabilityWindow();

    const prevProps = children.props;

    if (disabled && isNextAction) {
      return (
        <TooltipButton
          aria-label={ariaLabel}
          testIdPrefix={'date-range-next'}
          className={twMerge(
            'static inset-0 translate-y-0 cursor-not-allowed text-gray-950/30 hover:text-gray-950/30',
            className,
          )}
          tooltipText={t(
            messageToken || 'appointmentSlot.appointmentDate.maxDateTooltip',
            {
              maxDays,
              maxMonths: Math.ceil(maxDays / 30),
            },
          )}
          icon={'chevron_right'}
          iconSize={iconSize}
          displayType={'desktop'}
        />
      );
    }

    return (
      <BCustom
        aria-label={ariaLabel}
        data-testid={`date-range-${isNextAction ? 'next' : 'previous'}-btn`}
        slot={null}
        isDisabled={disabled}
        className={twMerge(
          'flex w-fit items-center justify-center border-0 p-1 text-gray-950',
          className,
        )}
        onPress={() => {
          if (!stateCalendar) {
            return;
          }

          onClick(stateCalendar);
        }}
      >
        {cloneElement(children, {
          ...prevProps,
          className: twMerge(prevProps.className, {
            'cursor-not-allowed !text-gray-400': disabled,
            'cursor-pointer': !disabled,
          }),
        })}
      </BCustom>
    );
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
);
CustomButtonActions.displayName = 'CustomButtonActions';

export const CustomDash = (): ReactElement => {
  const stateCalendar = useContext(DateRangePickerStateContext);

  return (
    <span
      data-testid={'date-range-dash-btn'}
      aria-hidden={'true'}
      className={twMerge('px-2 text-body-medium-semibold text-gray-950', {
        'text-primary-600': stateCalendar?.isOpen,
      })}
    >
      –
    </span>
  );
};
